import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { KatlasStatusPill } from "@/components/Pills";

export default function Diagnostics() {
  const [diag, setDiag] = useState(null);
  const [status, setStatus] = useState(null);

  const load = async () => {
    const [d, s] = await Promise.all([
      api.get("/katlas/diagnostics?probe=full"),
      api.get("/katlas/status"),
    ]);
    setDiag(d.data); setStatus(s.data);
  };

  useEffect(() => { load(); }, []);

  if (!diag || !status) return <div className="text-sm text-[hsl(var(--bc-muted))]">Probing KATLAS…</div>;

  return (
    <div data-testid="diagnostics-page">
      <section className="bc-rise">
        <p className="eyebrow">Diagnostics</p>
        <h1 className="font-display text-[44px] tracking-tight leading-[1.05] mt-3">KATLAS node state.</h1>
        <p className="mt-4 max-w-2xl text-[15px] text-[hsl(var(--bc-ink-soft))]">
          Redacted by design. Names and lengths only — never values. No bearer token, no Authorization header,
          no <span className="font-mono">cert_text</span>, no <span className="font-mono">cert_encoded</span>,
          no full <span className="font-mono">signed_receipt</span>.
        </p>
      </section>

      <section className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6 bc-rise bc-rise-d1">
        <div className="bc-card p-7 lg:col-span-5">
          <p className="eyebrow mb-3">Live status</p>
          <KatlasStatusPill status={status.status} label={status.label} />
          <div className="rule my-6" />
          <dl className="space-y-3 text-sm">
            <KV k="Mode" v={status.mode} />
            <KV k="Node URL present" v={String(status.node_url_present)} />
            <KV k="Bearer token present" v={String(status.bearer_token_present)} />
            <KV k="Token source used" v={status.token_source_used} />
            <KV k="Simulation fallback" v={String(status.allow_simulation_fallback)} />
            <KV k="Timeout (s)" v={String(status.timeout_seconds)} />
            <KV k="Stage" v={diag.stage} />
          </dl>
          <button onClick={load} className="bc-btn-text mt-6" data-testid="reprobe-katlas">Re-probe →</button>
        </div>

        <div className="bc-card p-7 lg:col-span-7">
          <p className="eyebrow mb-3">Runtime visibility — key names only</p>
          <p className="text-xs text-[hsl(var(--bc-muted))] mb-4">
            Stage 1 acceptance: <span className="font-mono">KATLAS_BEARER_TOKEN</span> should be visible
            with <span className="font-mono">value_length: 0</span> and <span className="font-mono">non_empty: false</span>.
          </p>
          <div className="border border-[hsl(var(--border))] rounded overflow-hidden" data-testid="runtime-keys-table">
            <div className="grid grid-cols-12 bg-[hsl(var(--bc-paper))] px-4 py-2 text-[11px] eyebrow">
              <div className="col-span-7">Name</div>
              <div className="col-span-3">Value length</div>
              <div className="col-span-2">Non-empty</div>
            </div>
            {diag.runtime_visibility.matching_env_keys.map((k) => (
              <div key={k.name} className="grid grid-cols-12 px-4 py-3 text-sm border-t border-[hsl(var(--border))]">
                <div className="col-span-7 font-mono">{k.name}</div>
                <div className="col-span-3 font-mono">{k.value_length}</div>
                <div className="col-span-2 font-mono">{String(k.non_empty)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="bc-card-flat p-7 lg:col-span-7" data-testid="redaction-policy">
          <p className="eyebrow mb-3">Redaction policy</p>
          <ul className="space-y-2 text-sm">
            {Object.entries(diag.redaction_policy).map(([k, v]) => (
              <li key={k} className="flex justify-between">
                <span>{k.replace(/_/g, " ")}</span>
                <span className="font-mono">{String(v)}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="bc-card-flat p-7 lg:col-span-5">
          <p className="eyebrow mb-3">KATLAS endpoints</p>
          <ul className="space-y-2 text-sm">
            <li><span className="eyebrow mr-2">GET</span> <span className="font-mono break-all">{diag.endpoints.status}</span></li>
            <li><span className="eyebrow mr-2">POST</span> <span className="font-mono break-all">{diag.endpoints.sign}</span></li>
            <li><span className="eyebrow mr-2">POST</span> <span className="font-mono break-all">{diag.endpoints.verify}</span></li>
          </ul>
        </div>
      </section>

      <section className="mt-10 bc-card p-7">
        <p className="eyebrow mb-3">Status mapping legend</p>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <li><span className="bc-status" data-tone="success">active</span> KATLAS node active</li>
          <li><span className="bc-status">unconfigured</span> KATLAS unconfigured — production credential not set</li>
          <li><span className="bc-status" data-tone="danger">auth_rejected</span> Node reachable; bearer token rejected</li>
          <li><span className="bc-status" data-tone="warn">unreachable</span> Node unreachable (DNS / timeout)</li>
          <li><span className="bc-status" data-tone="danger">node_error</span> Node returned 5xx</li>
        </ul>
      </section>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-[hsl(var(--bc-muted))] text-xs uppercase tracking-wider">{k}</dt>
      <dd className="font-mono text-xs text-[hsl(var(--bc-ink))]">{v}</dd>
    </div>
  );
}
